import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid } from '@mui/material';
import { Color } from '../../../../assets/color';
import { useNavigate, useParams } from 'react-router-dom';
import RouteHeader from '../../../../components/common/RouteHeader';
import * as SuperadminActions from '../../../../redux/actions/superadminAction';
import Swal from 'sweetalert2';
import { Clock, User, Globe, Monitor, Smartphone, FileText, Link2, MessageSquare, CheckCircle, AlertTriangle, ArrowRight } from 'lucide-react';

const BugDetail = () => {
    const { bugId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { bugReportByIdData, searchBugReportsData } = useSelector(state => state?.superadminReducer);

    const [comment, setComment] = useState('');
    const [statusToUpdate, setStatusToUpdate] = useState('');
    const [statusComment, setStatusComment] = useState('');
    const [showResolution, setShowResolution] = useState(false);
    const [showTestReport, setShowTestReport] = useState(false);
    const [relatedSearch, setRelatedSearch] = useState('');
    const [showRelatedDropdown, setShowRelatedDropdown] = useState(false);

    const [resolution, setResolution] = useState({
        resolution_notes: '', files_modified: '', fix_description: '', test_steps: '',
    });

    const [testReport, setTestReport] = useState({
        test_result: '', test_steps: '',
    });

    const bug = bugReportByIdData;

    useEffect(() => {
        if (bugId) dispatch(SuperadminActions.getBugReportById(bugId));
    }, [bugId]);

    const refreshBug = () => dispatch(SuperadminActions.getBugReportById(bugId));

    const handleAddComment = () => {
        if (!comment.trim()) return;
        dispatch(SuperadminActions.addBugComment({
            id: bugId,
            data: { message: comment },
            onComplete: () => { setComment(''); refreshBug(); },
        }));
    };

    const handleUpdateStatus = () => {
        if (!statusToUpdate) return;
        dispatch(SuperadminActions.updateBugStatus({
            id: bugId,
            data: { status: statusToUpdate, comment: statusComment || undefined },
            onComplete: () => { setStatusToUpdate(''); setStatusComment(''); refreshBug(); },
        }));
    };

    const handleAddResolution = () => {
        if (!resolution.fix_description && !resolution.resolution_notes) {
            Swal.fire({ icon: 'warning', text: 'Please add resolution details.', timer: 3000, showConfirmButton: false });
            return;
        }
        dispatch(SuperadminActions.addBugResolution({
            id: bugId,
            data: {
                ...resolution,
                files_modified: resolution.files_modified ? resolution.files_modified.split('\n').filter(f => f.trim()) : [],
            },
            onComplete: () => { setShowResolution(false); setResolution({ resolution_notes: '', files_modified: '', fix_description: '', test_steps: '' }); refreshBug(); },
        }));
    };

    const handleAddTestReport = () => {
        if (!testReport.test_result) {
            Swal.fire({ icon: 'warning', text: 'Please add test result.', timer: 3000, showConfirmButton: false });
            return;
        }
        dispatch(SuperadminActions.addBugTestReport({
            id: bugId,
            data: testReport,
            onComplete: () => { setShowTestReport(false); setTestReport({ test_result: '', test_steps: '' }); refreshBug(); },
        }));
    };

    const handleCloseBug = () => {
        dispatch(SuperadminActions.updateBugStatus({
            id: bugId,
            data: { status: 'Closed', comment: 'Issue verified and closed by Super Admin.' },
            onComplete: () => refreshBug(),
        }));
    };

    const handleRelatedSearch = (value) => {
        setRelatedSearch(value);
        if (value.length >= 2) {
            dispatch(SuperadminActions.searchBugReports(value));
            setShowRelatedDropdown(true);
        } else {
            setShowRelatedDropdown(false);
        }
    };

    const addRelatedBug = (relBug) => {
        const currentRelated = bug?.related_bugs?.map(b => b._id) || [];
        if (!currentRelated.includes(relBug._id)) {
            dispatch(SuperadminActions.updateBugReport({
                id: bugId,
                data: { related_bugs: [...currentRelated, relBug._id] },
                onComplete: () => { setRelatedSearch(''); setShowRelatedDropdown(false); refreshBug(); },
            }));
        }
    };

    const statusColors = {
        'Open': { bg: '#FEF3C7', color: '#D97706', border: '#FDE68A' },
        'In Review': { bg: '#EDE9FE', color: '#7C3AED', border: '#C4B5FD' },
        'Valid': { bg: '#DBEAFE', color: '#2563EB', border: '#93C5FD' },
        'In Progress': { bg: '#CFFAFE', color: '#0891B2', border: '#67E8F9' },
        'Testing': { bg: '#E0E7FF', color: '#4F46E5', border: '#A5B4FC' },
        'Closed': { bg: '#D1FAE5', color: '#059669', border: '#6EE7B7' },
        'Rejected': { bg: '#FEE2E2', color: '#DC2626', border: '#FCA5A5' },
    };

    const priorityColors = {
        'Critical': { bg: '#FEE2E2', color: '#DC2626' },
        'High': { bg: '#FEF3C7', color: '#D97706' },
        'Medium': { bg: '#DBEAFE', color: '#2563EB' },
        'Low': { bg: '#D1FAE5', color: '#059669' },
    };

    const commentTypeIcons = {
        'comment': <MessageSquare size={14} />,
        'status_change': <ArrowRight size={14} />,
        'resolution': <CheckCircle size={14} />,
        'test_report': <FileText size={14} />,
    };

    const commentTypeBg = {
        'comment': '#F8FAFC',
        'status_change': '#FFF7ED',
        'resolution': '#F0FDF4',
        'test_report': '#EFF6FF',
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    const inputStyle = {
        width: '100%', padding: '10px 14px', borderRadius: '8px', border: '2px solid #E2E8F0',
        fontSize: '14px', outline: 'none', fontFamily: 'inherit',
    };

    const sectionStyle = {
        backgroundColor: Color.white, borderRadius: '12px', padding: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '20px',
    };

    const headingStyle = {
        fontSize: '18px', fontWeight: '600', marginBottom: '16px',
        background: 'linear-gradient(to right, #6653E8, #9544EB)',
        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
    };

    if (!bug) {
        return (
            <>
                <RouteHeader title="Bug Detail" sub_title="Loading..." />
                <div style={{ textAlign: 'center', padding: '60px', color: '#72727B' }}>Loading bug details...</div>
            </>
        );
    }

    return (
        <>
            <RouteHeader title={`${bug.bug_id} - ${bug.title}`} sub_title={`Module: ${bug.module} | Reported: ${formatDate(bug.createdAt)}`} />

            {/* Status & Priority Badge Bar */}
            <div style={{ ...sectionStyle, display: 'flex', flexWrap: 'wrap', gap: '15px', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{
                        padding: '6px 16px', borderRadius: '20px', fontSize: '14px', fontWeight: '600',
                        backgroundColor: statusColors[bug.status]?.bg, color: statusColors[bug.status]?.color,
                        border: `1px solid ${statusColors[bug.status]?.border}`,
                    }}>
                        {bug.status}
                    </span>
                    <span style={{
                        padding: '6px 16px', borderRadius: '20px', fontSize: '14px', fontWeight: '600',
                        backgroundColor: priorityColors[bug.priority]?.bg, color: priorityColors[bug.priority]?.color,
                    }}>
                        {bug.priority} Priority
                    </span>
                    <span style={{ padding: '6px 16px', borderRadius: '20px', fontSize: '13px', backgroundColor: '#F1F5F9', color: '#64748B' }}>
                        📁 {bug.module}
                    </span>
                </div>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {bug.status !== 'Closed' && bug.status !== 'Rejected' && (
                        <div onClick={handleCloseBug} style={{
                            padding: '8px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500',
                            backgroundColor: '#10B981', color: '#FFF',
                        }}>
                            ✅ Close Issue
                        </div>
                    )}
                </div>
            </div>

            <Grid container spacing={3}>
                {/* Left Column - Bug Details */}
                <Grid item lg={8} md={7} sm={12} xs={12}>
                    {/* Description */}
                    <div style={sectionStyle}>
                        <h3 style={headingStyle}>📋 Bug Details</h3>
                        <div style={{ marginBottom: '20px' }}>
                            <p style={{ fontSize: '13px', fontWeight: '600', color: '#64748B', marginBottom: '4px' }}>Description</p>
                            <p style={{ fontSize: '14px', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{bug.description}</p>
                        </div>

                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <p style={{ fontSize: '13px', fontWeight: '600', color: '#64748B', marginBottom: '4px' }}>Steps to Reproduce</p>
                                <div style={{ padding: '12px', backgroundColor: '#F8FAFC', borderRadius: '8px', fontSize: '14px', whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                                    {bug.steps_to_reproduce}
                                </div>
                            </Grid>
                            <Grid item xs={6}>
                                <p style={{ fontSize: '13px', fontWeight: '600', color: '#64748B', marginBottom: '4px' }}>Expected Result</p>
                                <div style={{ padding: '12px', backgroundColor: '#F0FDF4', borderRadius: '8px', fontSize: '14px', whiteSpace: 'pre-wrap', border: '1px solid #BBF7D0' }}>
                                    {bug.expected_result}
                                </div>
                            </Grid>
                            <Grid item xs={6}>
                                <p style={{ fontSize: '13px', fontWeight: '600', color: '#64748B', marginBottom: '4px' }}>Actual Result</p>
                                <div style={{ padding: '12px', backgroundColor: '#FEF2F2', borderRadius: '8px', fontSize: '14px', whiteSpace: 'pre-wrap', border: '1px solid #FECACA' }}>
                                    {bug.actual_result}
                                </div>
                            </Grid>
                        </Grid>

                        {bug.error_message && (
                            <div style={{ marginTop: '16px' }}>
                                <p style={{ fontSize: '13px', fontWeight: '600', color: '#64748B', marginBottom: '4px' }}>Error Message</p>
                                <div style={{ padding: '12px', backgroundColor: '#1E293B', borderRadius: '8px', fontSize: '13px', color: '#F87171', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                                    {bug.error_message}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Resolution Details */}
                    {(bug.resolution_notes || bug.fix_description || bug.files_modified?.length > 0) && (
                        <div style={sectionStyle}>
                            <h3 style={headingStyle}>🔧 Resolution Details</h3>
                            {bug.fix_description && (
                                <div style={{ marginBottom: '12px' }}>
                                    <p style={{ fontSize: '13px', fontWeight: '600', color: '#64748B', marginBottom: '4px' }}>Fix Description</p>
                                    <p style={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>{bug.fix_description}</p>
                                </div>
                            )}
                            {bug.resolution_notes && (
                                <div style={{ marginBottom: '12px' }}>
                                    <p style={{ fontSize: '13px', fontWeight: '600', color: '#64748B', marginBottom: '4px' }}>Resolution Notes</p>
                                    <p style={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>{bug.resolution_notes}</p>
                                </div>
                            )}
                            {bug.files_modified?.length > 0 && (
                                <div style={{ marginBottom: '12px' }}>
                                    <p style={{ fontSize: '13px', fontWeight: '600', color: '#64748B', marginBottom: '4px' }}>Files Modified</p>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        {bug.files_modified.map((f, i) => (
                                            <span key={i} style={{ fontSize: '13px', fontFamily: 'monospace', padding: '4px 10px', backgroundColor: '#F1F5F9', borderRadius: '4px' }}>
                                                📄 {f}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {bug.test_steps && (
                                <div>
                                    <p style={{ fontSize: '13px', fontWeight: '600', color: '#64748B', marginBottom: '4px' }}>How to Test</p>
                                    <p style={{ fontSize: '14px', whiteSpace: 'pre-wrap', padding: '12px', backgroundColor: '#EFF6FF', borderRadius: '8px' }}>{bug.test_steps}</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Test Report */}
                    {bug.test_result && (
                        <div style={sectionStyle}>
                            <h3 style={headingStyle}>🧪 Test Report</h3>
                            <p style={{ fontSize: '14px', whiteSpace: 'pre-wrap', marginBottom: '8px' }}>{bug.test_result}</p>
                            {bug.tested_by && <p style={{ fontSize: '12px', color: '#72727B' }}>Tested by: {bug.tested_by} | {formatDate(bug.tested_at)}</p>}
                        </div>
                    )}

                    {/* Activity / Comments */}
                    <div style={sectionStyle}>
                        <h3 style={headingStyle}>💬 Activity Log ({bug.comments?.length || 0})</h3>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px', maxHeight: '400px', overflowY: 'auto' }}>
                            {bug.comments && bug.comments.length > 0 ? bug.comments.slice().reverse().map((c, idx) => (
                                <div key={idx} style={{
                                    padding: '12px 16px', borderRadius: '8px',
                                    backgroundColor: commentTypeBg[c.type] || '#F8FAFC',
                                    borderLeft: `3px solid ${c.type === 'status_change' ? '#F59E0B' : c.type === 'resolution' ? '#10B981' : c.type === 'test_report' ? '#3B82F6' : '#CBD5E1'}`,
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                            <span style={{ color: c.type === 'status_change' ? '#D97706' : c.type === 'resolution' ? '#059669' : c.type === 'test_report' ? '#2563EB' : '#64748B' }}>
                                                {commentTypeIcons[c.type]}
                                            </span>
                                            <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>{c.author}</span>
                                            <span style={{
                                                fontSize: '11px', padding: '1px 8px', borderRadius: '10px',
                                                backgroundColor: c.type === 'comment' ? '#E2E8F0' : c.type === 'status_change' ? '#FEF3C7' : c.type === 'resolution' ? '#D1FAE5' : '#DBEAFE',
                                                color: '#64748B', textTransform: 'capitalize',
                                            }}>
                                                {c.type?.replace('_', ' ')}
                                            </span>
                                        </div>
                                        <span style={{ fontSize: '12px', color: '#94A3B8' }}>{formatDate(c.createdAt)}</span>
                                    </div>
                                    <p style={{ fontSize: '14px', color: '#475569', whiteSpace: 'pre-wrap' }}>{c.message}</p>
                                </div>
                            )) : (
                                <p style={{ textAlign: 'center', color: '#94A3B8', padding: '20px' }}>No activity yet</p>
                            )}
                        </div>

                        {/* Add Comment */}
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <textarea
                                style={{ ...inputStyle, flex: 1, minHeight: '50px', resize: 'none' }}
                                placeholder="Add a comment..."
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                onFocus={(e) => e.target.style.borderColor = Color.primary}
                                onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                            />
                            <div onClick={handleAddComment} style={{
                                padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500',
                                background: 'linear-gradient(to right, #6653E8, #9544EB)', color: '#FFF',
                                display: 'flex', alignItems: 'center',
                            }}>
                                Send
                            </div>
                        </div>
                    </div>
                </Grid>

                {/* Right Column - Sidebar Info */}
                <Grid item lg={4} md={5} sm={12} xs={12}>
                    {/* Quick Info */}
                    <div style={sectionStyle}>
                        <h3 style={headingStyle}>ℹ️ Information</h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontSize: '13px', color: '#64748B' }}>Bug ID</span>
                                <span style={{ fontSize: '13px', fontWeight: '600', fontFamily: 'monospace', color: '#5045E5' }}>{bug.bug_id}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontSize: '13px', color: '#64748B' }}>Reported By</span>
                                <span style={{ fontSize: '13px', fontWeight: '500' }}>{bug.reported_by}</span>
                            </div>
                            {bug.affected_user && (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ fontSize: '13px', color: '#64748B' }}>Affected User</span>
                                    <span style={{ fontSize: '13px', fontWeight: '500' }}>{bug.affected_user}</span>
                                </div>
                            )}
                            {bug.affected_user_id && (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ fontSize: '13px', color: '#64748B' }}>User ID</span>
                                    <span style={{ fontSize: '13px', fontFamily: 'monospace' }}>{bug.affected_user_id}</span>
                                </div>
                            )}
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontSize: '13px', color: '#64748B' }}>Error Occurred</span>
                                <span style={{ fontSize: '12px' }}>{formatDate(bug.error_occurred_at)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontSize: '13px', color: '#64748B' }}>Created</span>
                                <span style={{ fontSize: '12px' }}>{formatDate(bug.createdAt)}</span>
                            </div>
                            {bug.closed_at && (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ fontSize: '13px', color: '#64748B' }}>Closed At</span>
                                    <span style={{ fontSize: '12px' }}>{formatDate(bug.closed_at)}</span>
                                </div>
                            )}
                            {bug.url_where_error && (
                                <div>
                                    <span style={{ fontSize: '13px', color: '#64748B' }}>URL</span>
                                    <p style={{ fontSize: '12px', fontFamily: 'monospace', wordBreak: 'break-all', marginTop: '2px' }}>{bug.url_where_error}</p>
                                </div>
                            )}
                            {bug.browser_info && (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ fontSize: '13px', color: '#64748B' }}>Browser</span>
                                    <span style={{ fontSize: '13px' }}>{bug.browser_info}</span>
                                </div>
                            )}
                            {bug.device_info && (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ fontSize: '13px', color: '#64748B' }}>Device</span>
                                    <span style={{ fontSize: '13px' }}>{bug.device_info}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Update Status */}
                    {bug.status !== 'Closed' && bug.status !== 'Rejected' && (
                        <div style={sectionStyle}>
                            <h3 style={headingStyle}>🔄 Update Status</h3>
                            <select
                                style={{ ...inputStyle, cursor: 'pointer', marginBottom: '10px' }}
                                value={statusToUpdate}
                                onChange={(e) => setStatusToUpdate(e.target.value)}
                            >
                                <option value="">Select new status...</option>
                                {['Open', 'In Review', 'Valid', 'In Progress', 'Testing', 'Closed', 'Rejected'].filter(s => s !== bug.status).map(s => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                            <input
                                style={{ ...inputStyle, marginBottom: '10px' }}
                                type="text"
                                placeholder="Add reason (optional)"
                                value={statusComment}
                                onChange={(e) => setStatusComment(e.target.value)}
                            />
                            <div onClick={handleUpdateStatus} style={{
                                padding: '10px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500',
                                background: 'linear-gradient(to right, #6653E8, #9544EB)', color: '#FFF', textAlign: 'center',
                            }}>
                                Update Status
                            </div>
                        </div>
                    )}

                    {/* Add Resolution */}
                    <div style={sectionStyle}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <h3 style={{ ...headingStyle, marginBottom: 0 }}>🔧 Resolution</h3>
                            <div onClick={() => setShowResolution(!showResolution)} style={{
                                fontSize: '12px', color: '#5045E5', cursor: 'pointer', fontWeight: '600',
                            }}>
                                {showResolution ? 'Cancel' : '+ Add'}
                            </div>
                        </div>
                        {showResolution && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                <textarea
                                    style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
                                    placeholder="What was done to fix this?"
                                    value={resolution.fix_description}
                                    onChange={(e) => setResolution({ ...resolution, fix_description: e.target.value })}
                                />
                                <textarea
                                    style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
                                    placeholder="Resolution notes..."
                                    value={resolution.resolution_notes}
                                    onChange={(e) => setResolution({ ...resolution, resolution_notes: e.target.value })}
                                />
                                <textarea
                                    style={{ ...inputStyle, minHeight: '60px', resize: 'vertical', fontFamily: 'monospace', fontSize: '12px' }}
                                    placeholder={"Files modified (one per line):\nsrc/pages/eye-scan/AnalyzeScan.jsx\nsrc/components/ScanSelect.jsx"}
                                    value={resolution.files_modified}
                                    onChange={(e) => setResolution({ ...resolution, files_modified: e.target.value })}
                                />
                                <textarea
                                    style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
                                    placeholder="How to test this fix?"
                                    value={resolution.test_steps}
                                    onChange={(e) => setResolution({ ...resolution, test_steps: e.target.value })}
                                />
                                <div onClick={handleAddResolution} style={{
                                    padding: '10px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500',
                                    backgroundColor: '#10B981', color: '#FFF', textAlign: 'center',
                                }}>
                                    Save Resolution
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Add Test Report */}
                    <div style={sectionStyle}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <h3 style={{ ...headingStyle, marginBottom: 0 }}>🧪 Test Report</h3>
                            <div onClick={() => setShowTestReport(!showTestReport)} style={{
                                fontSize: '12px', color: '#5045E5', cursor: 'pointer', fontWeight: '600',
                            }}>
                                {showTestReport ? 'Cancel' : '+ Add'}
                            </div>
                        </div>
                        {showTestReport && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                <textarea
                                    style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
                                    placeholder="Test steps followed..."
                                    value={testReport.test_steps}
                                    onChange={(e) => setTestReport({ ...testReport, test_steps: e.target.value })}
                                />
                                <textarea
                                    style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
                                    placeholder="Test result - Pass/Fail with details..."
                                    value={testReport.test_result}
                                    onChange={(e) => setTestReport({ ...testReport, test_result: e.target.value })}
                                />
                                <div onClick={handleAddTestReport} style={{
                                    padding: '10px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500',
                                    backgroundColor: '#3B82F6', color: '#FFF', textAlign: 'center',
                                }}>
                                    Save Test Report
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Related Issues */}
                    <div style={sectionStyle}>
                        <h3 style={headingStyle}>🔗 Related Issues</h3>
                        {bug.related_bugs?.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                                {bug.related_bugs.map((rb, idx) => (
                                    <div key={idx}
                                        onClick={() => navigate(`/bug-tracker/issues/${rb._id}`)}
                                        style={{
                                            padding: '10px 12px', borderRadius: '8px', backgroundColor: '#F8FAFC',
                                            cursor: 'pointer', border: '1px solid #E2E8F0', transition: 'background 0.2s',
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.backgroundColor = '#F0F0FF'}
                                        onMouseLeave={e => e.currentTarget.style.backgroundColor = '#F8FAFC'}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span style={{ fontSize: '12px', fontFamily: 'monospace', color: '#5045E5', fontWeight: '600' }}>{rb.bug_id}</span>
                                            <span style={{
                                                fontSize: '11px', padding: '2px 8px', borderRadius: '10px',
                                                backgroundColor: statusColors[rb.status]?.bg, color: statusColors[rb.status]?.color,
                                            }}>{rb.status}</span>
                                        </div>
                                        <p style={{ fontSize: '13px', marginTop: '2px' }}>{rb.title}</p>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p style={{ fontSize: '13px', color: '#94A3B8', marginBottom: '12px' }}>No related issues linked</p>
                        )}

                        {/* Search & Add Related */}
                        <div style={{ position: 'relative' }}>
                            <input
                                style={inputStyle}
                                type="text"
                                placeholder="Search to link related issue..."
                                value={relatedSearch}
                                onChange={(e) => handleRelatedSearch(e.target.value)}
                                onBlur={() => setTimeout(() => setShowRelatedDropdown(false), 200)}
                            />
                            {showRelatedDropdown && searchBugReportsData?.length > 0 && (
                                <div style={{
                                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                                    backgroundColor: '#FFF', border: '2px solid #E2E8F0', borderRadius: '8px',
                                    maxHeight: '200px', overflowY: 'auto', boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                                }}>
                                    {searchBugReportsData.filter(b => b._id !== bugId).map(b => (
                                        <div key={b._id}
                                            onClick={() => addRelatedBug(b)}
                                            style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #F1F5F9' }}
                                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#F0F0FF'}
                                            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#FFF'}
                                        >
                                            <span style={{ fontSize: '12px', fontFamily: 'monospace', color: '#5045E5', fontWeight: '600' }}>{b.bug_id}</span>
                                            <span style={{ fontSize: '13px', marginLeft: '8px' }}>{b.title}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </Grid>
            </Grid>
        </>
    );
};

export default BugDetail;
